import React, {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { Button } from '@material-ui/core';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { Table, Modal } from '~/components';
import { t, canURL } from '~/helpers';

import Form from './Form';
import * as S from './styles';

function Preset({
  data,
  components,
  arms,
  viscosidades,
  groups,
  onCallback,
  ehCarregamento,
}) {
  const [items, setItems] = useState(data);
  const [active, setActive] = useState([]);

  const modalRef = useRef(null);

  const CRUD = useMemo(
    () =>
      Object.keys(canURL()).some((url) => ['create', 'update'].includes(url)),
    []
  );

  const optionSN = useMemo(
    () => [
      { label: t('s.geral.opcoes.sim'), value: 'S' },
      { label: t('s.geral.opcoes.nao'), value: 'N' },
    ],
    []
  );

  const cells = useMemo(() => {
    const dataCells = ehCarregamento
      ? [
          {
            id: 'medidorpresetproduto',
            label: t('s.menu.configuracao.medidor'),
          },
          { id: 'idproduto', label: t('s.menu.configuracao.produto') },
          { id: 'nomeproduto', label: t('s.configuracao.produto.nomeproduto') },
        ]
      : [
          {
            id: 'medidorpresetproduto',
            label: t('s.menu.configuracao.medidor'),
          },
          { id: 'idproduto', label: t('s.menu.configuracao.produto') },
          {
            id: 'nomeabreviadoproduto',
            label: t('s.configuracao.produto.nomeproduto'),
          },
          {
            id: 'tagpresetproduto',
            label: t('s.operacao.tagrestricao.tagrestricaotag'),
          },
        ];

    return dataCells;
  }, [groups, arms, optionSN, components, ehCarregamento]);

  // Modify currently active item
  const handleChangeModifyActive = useCallback((value) => {
    setActive(value);
  }, []);

  // Remove active item
  const handleRemoveActive = useCallback(() => {
    setItems((state) => state.filter((item) => item.id !== active[0].id));
    setActive([]);
  }, [active]);

  // Open form for editing data
  const handleOpenModalToUpdateRow = useCallback(() => {
    modalRef.current.handleOpenModal({
      data: active[0],
      components,
      arms,
      viscosidades,
      groups,
      ehCarregamento,
    });
  }, [active, components, arms, viscosidades, groups, ehCarregamento]);

  // Update or create record in table
  const handleUpdateOrCreateInRow = useCallback(
    (value) => {
      const values = [...items];

      const index = values.findIndex((item) => item.id === value.id);

      if (index >= 0) values[index] = value;
      else values.push(value);

      setItems(values);
      setActive([]);
    },
    [items]
  );

  useEffect(() => {
    onCallback(items);
  }, [items]);

  return (
    <S.Wrapper>
      {CRUD && (
        <header>
          <Button
            variant="contained"
            color="primary"
            size="small"
            type="button"
            onClick={() =>
              modalRef.current.handleOpenModal({
                components,
                arms,
                viscosidades,
                groups,
                ehCarregamento,
              })
            }
          >
            {t('s.geral.botaoincluir')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            type="button"
            disabled={isEmpty(active) || isEmpty(components)}
            onClick={handleOpenModalToUpdateRow}
          >
            {t('s.geral.botaoalterar')}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            type="button"
            disabled={isEmpty(active) || isEmpty(components)}
            onClick={handleRemoveActive}
          >
            {t('s.geral.botaoexcluir')}
          </Button>
        </header>
      )}

      <Table
        noButton
        showPagination={false}
        paginate={99}
        cells={cells}
        data={items}
        onCallback={handleChangeModifyActive}
        unique="id"
        size="small"
      />

      <Modal
        form
        disableBackdropClick
        disableEscapeKeyDown
        title={t('s.menu.configuracao.produto')}
        maxWidth="xs"
        ref={modalRef}
        onForm={handleUpdateOrCreateInRow}
        component={Form}
        ehCarregamento={ehCarregamento}
      />
    </S.Wrapper>
  );
}

Preset.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  components: PropTypes.arrayOf(PropTypes.any).isRequired,
  arms: PropTypes.arrayOf(PropTypes.any).isRequired,
  groups: PropTypes.arrayOf(PropTypes.any).isRequired,
  viscosidades: PropTypes.arrayOf(PropTypes.any).isRequired,
  onCallback: PropTypes.func.isRequired,
  ehCarregamento: PropTypes.bool.isRequired,
};

export default memo(Preset);
